<script lang="ts">
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiCalendarPlus, mdiFolderUpload, mdiPrinterOutline } from "@mdi/js"
  import CreateAppointmentOverlay from "../../../../_shared/_modules/create-appointment-overlay"
  import { buildAppointmentCreateInit } from "../../../../_shared/_logic/init"
  import WorkOrderBatchUploadOverlay from "./_modules/work-order-batch-upload-overlay"
  import { fetchNextWorkOrderNumber } from "../../../../../../../../api/kehrblatt/work-orders"
  import { fetchOrderTypes } from "../../../../../../../../api/kehrblatt/order-types"
  import {
    WORK_ORDER_ACTION_TOOLTIP_CLASS,
    workOrderActionTooltips,
  } from "../../../../../../_shared/_logic/components-work-orders-action-tooltips"
  import { invalidateWorkOrdersOverview } from "../../_shared/_logic/invalidation"
  import type { WorkingHoursByUser } from "../../../../../../../../_shared/_logic/form-state"
  import type { BatchPlanningEmployeeContext } from "../../_shared/_logic/batch-planning-context"

  type FilterDisplayMode = "compact" | "detailed"

  interface Props {
    organization: OrganizationFrame
    trialRestrictionsActive?: boolean | null
    properties: Property[]
    customers: Customer[]
    contactPoints?: ContactPoint[]
    orderTypes: OrderType[]
    planNowEmployees: { id: string; label: string }[]
    scheduleEvents?: ScheduleEvent[]
    planningSettings?: PlanningSettings | null
    workingHoursByUser?: WorkingHoursByUser
    allEmployeeIds?: string[]
    nextOrderNumber: string | null
    canManageScheduling?: boolean
    canPlanAppointments?: boolean
    planningUnavailableReason?: string | null
    title?: string
    filterMode?: FilterDisplayMode | null
    onFilterModeChange?: ((value: FilterDisplayMode) => void) | null
    onPrint?: (() => void) | null
    printPending?: boolean | null
    autoOpenBatchUploadKey?: string | null
    onAutoBatchUploadConsumed?: (() => void) | null
    ensureMetadataLoaded?: (() => Promise<void> | void) | null
    resolveBatchPlanningEmployeeContext?:
      | (() => Promise<BatchPlanningEmployeeContext> | BatchPlanningEmployeeContext)
      | null
  }

  let {
    organization,
    trialRestrictionsActive = false,
    properties,
    customers,
    contactPoints = [],
    orderTypes,
    planNowEmployees,
    scheduleEvents = [],
    planningSettings = null,
    workingHoursByUser = {},
    allEmployeeIds = [],
    nextOrderNumber,
    canManageScheduling = true,
    canPlanAppointments = true,
    planningUnavailableReason = null,
    title = "Aufträge",
    filterMode = null,
    onFilterModeChange = null,
    onPrint = null,
    printPending = false,
    autoOpenBatchUploadKey = null,
    onAutoBatchUploadConsumed = null,
    ensureMetadataLoaded = null,
    resolveBatchPlanningEmployeeContext = null,
  }: Props = $props()

  let openingBatchUpload = $state(false)
  let openingCreate = $state(false)
  let lastAutoOpenBatchUploadKey = ""
  const showFilterModeToggle = $derived(Boolean(filterMode && onFilterModeChange))
  const printActionDisabled = $derived(Boolean(printPending))
  const printTooltipContent = $derived(
    printPending ? "Der Tourenzettel wird gerade erstellt." : workOrderActionTooltips.headerPrint,
  )
  const disabledPrintTooltip = $derived(
    printActionDisabled
      ? {
          content: printTooltipContent,
          className: WORK_ORDER_ACTION_TOOLTIP_CLASS,
        }
      : false,
  )
  const printCtaTooltip = $derived(printActionDisabled ? null : printTooltipContent)
  const planningActionDisabled = $derived(
    openingBatchUpload || openingCreate || !canPlanAppointments,
  )
  const planningDisabledReason = $derived(
    !canPlanAppointments
      ? (planningUnavailableReason ?? "Die Terminplanung ist derzeit nicht verfügbar.")
      : "Die Terminplanung wird gerade geöffnet.",
  )
  const disabledPlanningTooltip = $derived(
    planningActionDisabled
      ? {
          content: planningDisabledReason,
          className: WORK_ORDER_ACTION_TOOLTIP_CLASS,
        }
      : false,
  )
  const showDisabledPlanningActions = $derived(!canPlanAppointments && canManageScheduling)
  const showPlanningActions = $derived(canPlanAppointments || showDisabledPlanningActions)
  const filterModeOptions: Array<{ value: FilterDisplayMode; label: string }> = [
    { value: "compact", label: "Kompakt" },
    { value: "detailed", label: "Ausführlich" },
  ]

  const resolveDefaultOrderNumber = async () => {
    if (!organization?.id) return nextOrderNumber ?? null

    try {
      const response = await fetchNextWorkOrderNumber(organization.id)

      return response?.next_order_number ?? nextOrderNumber ?? null
    } catch (err) {
      console.error("next_order_number_fetch_failed", err)

      return nextOrderNumber ?? null
    }
  }

  const resolveOrderTypes = async () => {
    await ensureMetadataLoaded?.()

    if (orderTypes.length || !organization?.id) return orderTypes

    try {
      return await fetchOrderTypes(organization.id)
    } catch (err) {
      console.error("order_types_fetch_failed", err)

      return orderTypes
    }
  }

  const currentBatchPlanningEmployeeContext = (): BatchPlanningEmployeeContext => ({
    employees: planNowEmployees,
    workingHoursByUser,
    allEmployeeIds,
  })

  const resolveFreshBatchPlanningEmployeeContext =
    async (): Promise<BatchPlanningEmployeeContext> => {
      if (planNowEmployees.length > 0 || !resolveBatchPlanningEmployeeContext) {
        return currentBatchPlanningEmployeeContext()
      }

      try {
        return await resolveBatchPlanningEmployeeContext()
      } catch (err) {
        console.error("batch_planning_employee_context_refresh_failed", err)

        return currentBatchPlanningEmployeeContext()
      }
    }

  const openCreateOverlay = async () => {
    if (!organization?.id || planningActionDisabled) return

    openingCreate = true
    try {
      const resolvedOrderTypes = await resolveOrderTypes()
      const defaultOrderNumber = await resolveDefaultOrderNumber()
      openOverlay({
        title: "Termin planen",
        content: CreateAppointmentOverlay,
        bodyPadding: "none",
        closeOnParentOpen: true,
        isDark: true,
        properties: {
          init: buildAppointmentCreateInit({
            organizationId: organization.id,
            employees: planNowEmployees,
            workOrders: [],
            properties,
            customers,
            contactPoints,
            scheduleEvents,
            workingHoursByUser,
            candidateEmployeeIds: allEmployeeIds?.length
              ? allEmployeeIds
              : planNowEmployees.map((employee) => employee.id).filter(Boolean),
            defaultEmployeeId: null,
            defaultStart: null,
            defaultEnd: null,
            defaultFlexType: "HARD" as ScheduleEventFlexType,
            defaultStatus: "RESERVED" as ScheduleEventStatus,
            defaultWorkOrderId: null,
            defaultPropertyId: null,
            flexSlotMinutes: planningSettings?.flex_slot_minutes ?? null,
            flexSlotsPerDay: planningSettings?.flex_slots_per_day ?? null,
            allowNewWorkOrder: true,
            defaultWorkOrderSource: "new",
            orderTypes: resolvedOrderTypes,
            defaultOrderNumber,
          }),
          onCreated: async () => {
            await invalidateWorkOrdersOverview(organization.id)
          },
        },
      })
    } finally {
      openingCreate = false
    }
  }

  const openBatchUpload = async () => {
    if (!organization?.id || planningActionDisabled) return

    openingBatchUpload = true
    try {
      const resolvedOrderTypes = await resolveOrderTypes()
      const employeeContext = await resolveFreshBatchPlanningEmployeeContext()

      if (!employeeContext.employees.length) {
        newNotification({
          class: "error",
          html: "Es sind noch keine aktiven Mitarbeitenden verfügbar.",
        })

        return
      }

      openOverlay({
        title: "Sammelplanung",
        content: WorkOrderBatchUploadOverlay,
        bodyPadding: "none",
        closeOnParentOpen: true,
        defaultFullscreen: true,
        properties: {
          organizationId: organization.id,
          trialRestrictionsActive,
          properties,
          customers,
          contactPoints,
          orderTypes: resolvedOrderTypes,
          defaultOrderNumber: nextOrderNumber,
          employees: employeeContext.employees,
          workingHoursByUser: employeeContext.workingHoursByUser,
          onCompleted: async () => {
            await invalidateWorkOrdersOverview(organization.id)
          },
        },
      })
    } finally {
      openingBatchUpload = false
    }
  }

  const handlePrintAction = () => {
    if (printActionDisabled) return

    onPrint?.()
  }

  $effect(() => {
    if (!autoOpenBatchUploadKey || autoOpenBatchUploadKey === lastAutoOpenBatchUploadKey) return

    if (!organization?.id) return

    lastAutoOpenBatchUploadKey = autoOpenBatchUploadKey

    if (!canPlanAppointments) {
      newNotification({
        class: "error",
        html:
          planningUnavailableReason ??
          "Die Sammelplanung ist für Ihren Mitarbeiterbereich nicht verfügbar.",
      })
      onAutoBatchUploadConsumed?.()

      return
    }

    void openBatchUpload().then(() => {
      onAutoBatchUploadConsumed?.()
    })
  })
</script>

<div class="section-header">
  <SectionTitle
    {title}
    brightBackground={true}
    helpModalTextId="workOrders"
    helpModalTooltip="Wie Aufträge, Termine und Ankündigungspläne zusammenhängen."
  >
    {#snippet buttonSnippet()}
      {#if showFilterModeToggle}
        <ToggleCTA
          options={filterModeOptions}
          value={filterMode ?? "compact"}
          darkMode={false}
          onChange={(value) => {
            const nextValue = `${value ?? filterMode ?? "compact"}`.toLowerCase()
            onFilterModeChange?.(nextValue === "detailed" ? "detailed" : "compact")
          }}
        />
      {/if}
      {#if onPrint && !showPlanningActions}
        <span class="print-action-tooltip-anchor" use:tooltip={disabledPrintTooltip}>
          <CTA
            type="secondaryButton"
            content={printPending ? "PDF wird erstellt..." : "Termine drucken"}
            onClick={handlePrintAction}
            tooltipContent={printCtaTooltip}
            tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
            disabled={printActionDisabled}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiPrinterOutline} />
            {/snippet}
          </CTA>
        </span>
      {/if}
      {#if showPlanningActions}
        <span class="planning-action-tooltip-anchor" use:tooltip={disabledPlanningTooltip}>
          <CTA
            type="primaryButton"
            content={openingBatchUpload || openingCreate ? "Planung laden..." : "Termine planen"}
            onClick={openBatchUpload}
            tooltipContent={canPlanAppointments
              ? workOrderActionTooltips.headerBatchPlanning
              : null}
            tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
            disabled={planningActionDisabled}
            options={{
              label: "Weitere Planungsaktionen",
              disabled: openingBatchUpload || openingCreate,
              disabledReason: planningDisabledReason,
              tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
              items: [
                {
                  id: "plan-appointment",
                  label: "Termin planen",
                  icon: mdiCalendarPlus,
                  disabled: planningActionDisabled,
                  reason: planningActionDisabled
                    ? planningDisabledReason
                    : workOrderActionTooltips.headerCreate,
                  tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
                  onClick: () => {
                    void openCreateOverlay()
                  },
                },
                ...(onPrint
                  ? [
                      {
                        id: "print-tour-sheet",
                        label: printPending ? "PDF wird erstellt..." : "Termine drucken",
                        icon: mdiPrinterOutline,
                        disabled: printActionDisabled,
                        reason: printTooltipContent,
                        tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
                        onClick: handlePrintAction,
                      },
                    ]
                  : []),
              ],
            }}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiFolderUpload} />
            {/snippet}
          </CTA>
        </span>
      {/if}
    {/snippet}
  </SectionTitle>
</div>

<style lang="less">
  .section-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .print-action-tooltip-anchor,
  .planning-action-tooltip-anchor {
    display: inline-flex;

    :global(button[disabled]) {
      pointer-events: none;
    }
  }
</style>
